import { useEffect, useState } from 'react';
import type { ProductType } from '@econtractor/types';
import * as apiProduct from 'api/apiProducts';

let cachedProductsMap: Record<number, ProductType> | null = null;
let loadPromise: Promise<Record<number, ProductType>> | null = null;

const fetchProductsMap = async (): Promise<Record<number, ProductType>> => {
    if (cachedProductsMap) return cachedProductsMap;
    if (loadPromise) return loadPromise;

    loadPromise = (async () => {
        const resProducts = await apiProduct.getList({
            order: 'order by prod_name',
            onPage: 99999999,
            status: [1],
        });
        const map: Record<number, ProductType> = {};
        (resProducts?.products || []).forEach((product) => {
            map[Number(product.ID)] = product;
        });
        cachedProductsMap = map;
        loadPromise = null;
        return map;
    })();

    return loadPromise;
};

/** Loads products once per app session; subsequent mounts reuse the cache. */
export const useTemplateProductsMap = () => {
    const [productsMap, setProductsMap] = useState<Record<number, ProductType>>(
        () => cachedProductsMap || {},
    );
    const [loading, setLoading] = useState(() => !cachedProductsMap);

    useEffect(() => {
        let cancelled = false;

        void (async () => {
            const map = await fetchProductsMap();
            if (cancelled) return;
            setProductsMap(map);
            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return { productsMap, loading };
};
